import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  AllocateMachineBody,
  CreateMachineBody,
  DeallocateMachineBody,
  MachineAllocation,
  MachineDetail,
  MachineListItem,
  MachinesListQuery,
  UpdateMachineBody,
} from '@/types/machines';
import type { PaginatedResult } from '@/types/roles';

/**
 * Plan 11-02 — typed wrappers around the 9 `/api/machines` endpoints
 * defined in Plan 11-01.
 *
 * Each wrapper:
 *  - Throws an `ApiError` (via `toApiError`) on non-2xx so consumers can
 *    `instanceof`-switch on `e.status`.
 *  - Returns the typed response object on success.
 *
 * Permission gating is UI-only convenience (T-11-16). The backend re-checks
 * every permission via `[RequirePermission(...)]` (Plan 11-01).
 */

function buildListUrl(q: MachinesListQuery): string {
  const params = new URLSearchParams();
  if (q.q !== undefined && q.q !== '') params.set('q', q.q);
  if (q.typeId !== undefined) params.set('typeId', String(q.typeId));
  if (q.siteId !== undefined) params.set('siteId', String(q.siteId));
  if (q.status !== undefined) params.set('status', q.status);
  params.set('page', String(q.page ?? 1));
  params.set('pageSize', String(q.pageSize ?? 20));
  return `/api/machines?${params.toString()}`;
}

export async function listMachinesRequest(
  query: MachinesListQuery = {},
): Promise<PaginatedResult<MachineListItem>> {
  try {
    const { data } = await api.get<PaginatedResult<MachineListItem>>(buildListUrl(query));
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getMachineRequest(id: number): Promise<MachineDetail> {
  try {
    const { data } = await api.get<MachineDetail>(`/api/machines/${String(id)}`);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function getMachineAllocationsRequest(
  id: number,
): Promise<readonly MachineAllocation[]> {
  try {
    const { data } = await api.get<readonly MachineAllocation[]>(
      `/api/machines/${String(id)}/allocations`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function createMachineRequest(body: CreateMachineBody): Promise<MachineDetail> {
  try {
    const { data } = await api.post<MachineDetail>('/api/machines', body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function updateMachineRequest(
  id: number,
  body: UpdateMachineBody,
): Promise<MachineDetail> {
  try {
    const { data } = await api.put<MachineDetail>(`/api/machines/${String(id)}`, body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

export async function deactivateMachineRequest(id: number): Promise<void> {
  try {
    await api.delete(`/api/machines/${String(id)}`);
  } catch (e) {
    throw toApiError(e);
  }
}

export async function reactivateMachineRequest(id: number): Promise<void> {
  try {
    await api.post(`/api/machines/${String(id)}/reactivate`);
  } catch (e) {
    throw toApiError(e);
  }
}

export async function allocateMachineRequest(id: number, body: AllocateMachineBody): Promise<void> {
  try {
    await api.post(`/api/machines/${String(id)}/allocate`, body);
  } catch (e) {
    throw toApiError(e);
  }
}

export async function deallocateMachineRequest(
  id: number,
  body: DeallocateMachineBody,
): Promise<void> {
  try {
    await api.post(`/api/machines/${String(id)}/deallocate`, body);
  } catch (e) {
    throw toApiError(e);
  }
}
